import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import LayoutComponent from './LayoutComponent';

const ContentArea = ({ 
  children,
  breadcrumb = null,
  title = null,
  subtitle = null,
  leftSidebarWidth = 15,
  rightSidebarWidth = 15,
  isDarkMode = false,
  className = "",
  ...props 
}) => {
  const currentTheme = useTheme(isDarkMode);
  // Estilos personalizados específicos del ContentArea (además de los estilos base)
  const contentCustomStyles = {
    marginLeft: `${leftSidebarWidth-10}px`, // Dynamic offset for left sidebar
    marginTop: '12px',   // Offset for header (64px) + small gap (4px)
    marginRight: `${rightSidebarWidth + 20}px`, // Dynamic offset for right sidebar + extra padding
    minHeight: 'calc(100vh - 32px)',
    paddingBottom: '20px', // Extra bottom padding to prevent overlap
    ...props.style
  };

  const breadcrumbStyles = {
    fontSize: currentTheme.typography.fontSize.sm,
    color: currentTheme.colors.text.secondary,
    marginBottom: currentTheme.spacing.sm, // Reducido 50% (de md a sm)
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const titleStyles = {
    fontSize: currentTheme.typography.fontSize['4xl'],
    fontWeight: currentTheme.typography.fontWeight.bold,
    color: currentTheme.colors.text.primary,
    marginBottom: currentTheme.spacing.md, // Reducido 50% (de lg a md)
    lineHeight: currentTheme.typography.lineHeight.tight
  };

  const subtitleStyles = {
    fontSize: currentTheme.typography.fontSize.lg,
    color: currentTheme.colors.text.secondary,
    marginBottom: currentTheme.spacing.md, // Reducido 50% (de xl a lg)
    lineHeight: currentTheme.typography.lineHeight.normal
  };

  const codeBlockStyles = {
    backgroundColor: currentTheme.colors.code.background,
    border: `1px solid ${currentTheme.colors.code.border}`,
    borderRadius: currentTheme.borderRadius.lg,
    padding: currentTheme.spacing.md,
    margin: `${currentTheme.spacing.md} 0`,
    fontFamily: currentTheme.typography.fontFamily.mono,
    fontSize: currentTheme.typography.fontSize.sm,
    lineHeight: currentTheme.typography.lineHeight.normal,
    overflow: 'auto',
    position: 'relative'
  };

  const codeLineStyles = {
    display: 'block',
    padding: '2px 0',
    color: currentTheme.colors.code.text
  };

  const terminalStyles = {
    backgroundColor: currentTheme.colors.terminal.background,
    border: `1px solid ${currentTheme.colors.terminal.border}`,
    borderRadius: currentTheme.borderRadius.lg,
    margin: `${currentTheme.spacing.md} 0`,
    overflow: 'hidden',
    fontFamily: currentTheme.typography.fontFamily.mono
  };

  const terminalHeaderStyles = {
    backgroundColor: currentTheme.colors.terminal.border,
    padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${currentTheme.colors.terminal.border}`
  };

  const terminalTitleStyles = {
    color: currentTheme.colors.terminal.title,
    fontSize: currentTheme.typography.fontSize.sm,
    fontWeight: currentTheme.typography.fontWeight.medium
  };

  const terminalButtonsStyles = {
    display: 'flex',
    gap: currentTheme.spacing.sm
  };

  const terminalButtonStyles = {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer'
  };

  const terminalContentStyles = {
    padding: currentTheme.spacing.md,
    color: currentTheme.colors.terminal.text,
    fontSize: currentTheme.typography.fontSize.sm,
    lineHeight: currentTheme.typography.lineHeight.normal
  };

  const paragraphStyles = {
    marginBottom: currentTheme.spacing.md,
    color: currentTheme.colors.text.primary,
    fontSize: currentTheme.typography.fontSize.base
  };

  const headingStyles = {
    color: currentTheme.colors.text.primary,
    fontWeight: currentTheme.typography.fontWeight.semibold,
    marginTop: currentTheme.spacing.lg,
    marginBottom: currentTheme.spacing.md
  };

  const h2Styles = {
    ...headingStyles,
    fontSize: currentTheme.typography.fontSize['2xl']
  };

  const h3Styles = {
    ...headingStyles,
    fontSize: currentTheme.typography.fontSize.xl
  };

  const linkStyles = {
    color: currentTheme.colors.text.link,
    textDecoration: 'none',
    transition: 'color 0.2s ease-in-out'
  };

  const handleLinkHover = (e) => {
    e.target.style.color = currentTheme.colors.text.linkHover;
  };

  const handleLinkLeave = (e) => {
    e.target.style.color = currentTheme.colors.text.link;
  };

  // Helper function to render code blocks
  const renderCodeBlock = (code, language = 'text') => {
    const lines = code.split('\n');
    return (
      <div style={codeBlockStyles}>
        {lines.map((line, index) => (
          <div key={index} style={codeLineStyles}>
            <span style={{ 
              color: currentTheme.colors.code.comment, 
              marginRight: currentTheme.spacing.md,
              userSelect: 'none'
            }}>
              {(index + 1).toString().padStart(2, '0')}
            </span>
            <span>{line}</span>
          </div>
        ))}
      </div>
    );
  };

  // Helper function to render terminal
  const renderTerminal = (content, title = 'Terminal') => (
    <div style={terminalStyles}>
      <div style={terminalHeaderStyles}>
        <div style={terminalTitleStyles}>{title}</div>
        <div style={terminalButtonsStyles}>
          <button style={{...terminalButtonStyles, backgroundColor: '#ff5f57'}} />
          <button style={{...terminalButtonStyles, backgroundColor: '#ffbd2e'}} />
          <button style={{...terminalButtonStyles, backgroundColor: '#28ca42'}} />
        </div>
      </div>
      <div style={terminalContentStyles}>
        {content}
      </div>
    </div>
  );

  return (
    <LayoutComponent 
      componentType="content" 
      isDarkMode={isDarkMode}
      className={className}
      customStyles={contentCustomStyles}
    >
      {/* Breadcrumb */}
      {breadcrumb && (
        <div style={breadcrumbStyles}>
          {breadcrumb}
        </div>
      )}

      {/* Title */}
      {title && (
        <h1 style={titleStyles}>
          {title}
        </h1>
      )}

      {/* Subtitle */}
      {subtitle && (
        <p style={subtitleStyles}>
          {subtitle}
        </p>
      )}

      {/* Content */}
      {children && (
        <div>
          {children}
        </div>
      )}
    </LayoutComponent>
  );
};

// Export helper components for common content patterns
export const CodeBlock = ({ children, language = 'text', isDarkMode = false }) => {
  const currentTheme = useTheme(isDarkMode);
  
  const codeBlockStyles = {
    backgroundColor: currentTheme.colors.code.background,
    border: `1px solid ${currentTheme.colors.code.border}`,
    borderRadius: currentTheme.borderRadius.lg,
    padding: currentTheme.spacing.md,
    margin: `${currentTheme.spacing.md} 0`,
    fontFamily: currentTheme.typography.fontFamily.mono,
    fontSize: currentTheme.typography.fontSize.sm,
    lineHeight: currentTheme.typography.lineHeight.normal,
    overflow: 'auto'
  };

  return (
    <div style={codeBlockStyles}>
      {children}
    </div>
  );
};

export const Terminal = ({ children, title = 'Terminal', isDarkMode = false }) => {
  const currentTheme = useTheme(isDarkMode);
  
  const terminalStyles = {
    backgroundColor: currentTheme.colors.terminal.background,
    border: `1px solid ${currentTheme.colors.terminal.border}`,
    borderRadius: currentTheme.borderRadius.lg,
    margin: `${currentTheme.spacing.md} 0`,
    overflow: 'hidden',
    fontFamily: currentTheme.typography.fontFamily.mono
  };

  const terminalHeaderStyles = {
    backgroundColor: currentTheme.colors.terminal.border,
    padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${currentTheme.colors.terminal.border}`
  };

  const terminalTitleStyles = {
    color: currentTheme.colors.terminal.title,
    fontSize: currentTheme.typography.fontSize.sm,
    fontWeight: currentTheme.typography.fontWeight.medium
  };

  const terminalButtonsStyles = {
    display: 'flex',
    gap: currentTheme.spacing.sm
  };

  const terminalButtonStyles = {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: 'none'
  };

  const terminalContentStyles = {
    padding: currentTheme.spacing.md,
    color: currentTheme.colors.terminal.text,
    fontSize: currentTheme.typography.fontSize.sm,
    lineHeight: currentTheme.typography.lineHeight.normal
  };

  return (
    <div style={terminalStyles}>
      <div style={terminalHeaderStyles}>
        <div style={terminalTitleStyles}>{title}</div>
        <div style={terminalButtonsStyles}>
          <div style={{...terminalButtonStyles, backgroundColor: '#ff5f57'}} />
          <div style={{...terminalButtonStyles, backgroundColor: '#ffbd2e'}} />
          <div style={{...terminalButtonStyles, backgroundColor: '#28ca42'}} />
        </div>
      </div>
      <div style={terminalContentStyles}>
        {children}
      </div>
    </div>
  );
};

export const Heading = ({ level = 2, children, isDarkMode = false, style = null }) => {
  const currentTheme = useTheme(isDarkMode);
  
  const headingStyles = {
    color: currentTheme.colors.text.primary,
    fontWeight: currentTheme.typography.fontWeight.semibold,
    marginTop: currentTheme.spacing.lg,
    marginBottom: currentTheme.spacing.md
  };

  const levelStyles = {
    1: { ...headingStyles, fontSize: currentTheme.typography.fontSize['3xl'] },
    2: { ...headingStyles, fontSize: currentTheme.typography.fontSize['2xl'] },
    3: { ...headingStyles, fontSize: currentTheme.typography.fontSize.xl },
    4: { ...headingStyles, fontSize: currentTheme.typography.fontSize.lg }
  };

  const Tag = `h${level}`;
  
  return (
    <Tag style={{ ...levelStyles[level], ...(style || {}) }}>
      {children}
    </Tag>
  );
};

export const Paragraph = ({ children, isDarkMode = false }) => {
  const currentTheme = useTheme(isDarkMode);
  
  const paragraphStyles = {
    marginBottom: currentTheme.spacing.md,
    color: currentTheme.colors.text.primary,
    fontSize: currentTheme.typography.fontSize.base,
    lineHeight: currentTheme.typography.lineHeight.relaxed
  };

  return (
    <p style={paragraphStyles}>
      {children}
    </p>
  );
};

export default ContentArea;
